<template>
  <span
    v-if="isV2021_1"
    :class="[$style.v20211chip, $style[`v20211chip--${v2021_1Modifier}`]]"
  >
    {{ planText }}
  </span>
  <span v-else-if="isV2022_1" :class="$style.v20221chip">
    <IconV20221Plan :plan="props.plan" :class="$style.v20221chip__icon" />
    <p :class="$style.v20221chip__text">{{ planText }}</p>
  </span>
  <span v-else-if="isV2023_1" :class="$style.v20231chip">
    <IconV20231Plan :plan="props.plan" :class="$style.v20231chip__icon" />
    <p :class="$style.v20231chip__text">{{ planText }}</p>
  </span>
  <span v-else-if="isV2022_2" :class="$style.v20222chip">{{ planText }}</span>
  <span
    v-else-if="isV2023_1ManagerOnlyFree"
    :class="$style.v20231ManageOnlyFreeChip"
    >{{ planText }}</span
  >
</template>

<script setup lang="ts">
import { computed } from 'vue'
import IconV20221Plan from '@/components/icons/IconV20221Plan.vue'
import IconV20231Plan from '@/components/icons/IconV20231Plan.vue'
import { Plan } from '@/types/schema.generated'

const props = defineProps<{
  plan: Plan
}>()

const isV2021_1 = computed(() => {
  const plans: Plan[] = [
    Plan.V2021_1Standard,
    Plan.V2021_1Premium,
    Plan.V2021_1Enterprise,
  ]

  return plans.includes(props.plan)
})

const v2021_1Modifier = computed(() => {
  switch (props.plan) {
    case Plan.V2021_1Standard:
      return 'standard'
    case Plan.V2021_1Premium:
      return 'premium'
    case Plan.V2021_1Enterprise:
      return 'enterprise'
    default:
      return ''
  }
})

const isV2022_1 = computed(() => {
  const plans: Plan[] = [
    Plan.V2022_1Pr,
    Plan.V2022_1Entry,
    Plan.V2022_1Pro,
    Plan.V2022_1Enterprise,
  ]

  return plans.includes(props.plan)
})

const isV2023_1 = computed(() => {
  const plans: Plan[] = [
    Plan.V2023_1SkillForce,
    Plan.V2023_1Expert,
    Plan.V2023_1Master,
    Plan.V2023_1Enterprise,
  ]

  return plans.includes(props.plan)
})

const isV2022_2 = computed(() => props.plan === Plan.V2022_2ManageOnlyFree)

const isV2023_1ManagerOnlyFree = computed(
  () => props.plan === Plan.V2023_1ManageOnlyFree
)

const planTextMap: Record<Plan, string> = {
  [Plan.V2021_1Standard]: 'スタンダードプラン',
  [Plan.V2021_1Premium]: 'プレミアムプラン',
  [Plan.V2021_1Enterprise]: 'エンタープライズプラン',
  [Plan.V2022_1Pr]: 'PRプラン',
  [Plan.V2022_1Entry]: 'Entryプラン',
  [Plan.V2022_1Pro]: 'Proプラン',
  [Plan.V2022_1Enterprise]: 'Enterpriseプラン',
  [Plan.V2022_2ManageOnlyFree]: '管理者専用無料プラン',
  [Plan.V2023_1SkillForce]: 'スキルフォースプラン',
  [Plan.V2023_1Expert]: 'エキスパートプラン',
  [Plan.V2023_1Master]: 'マスタープラン',
  [Plan.V2023_1Enterprise]: 'Enterpriseプラン',
  [Plan.V2023_1ManageOnlyFree]: '管理者専用無料プラン',
}

const planText = computed(() => planTextMap[props.plan])
</script>

<style lang="scss" module>
.v20211chip {
  display: inline-block;
  padding: 0.5em 1em;
  border-radius: $border-radius-rounder;
  font-size: 1em;
  font-weight: bold;
  line-height: 1;
  white-space: nowrap;

  &--standard {
    background-color: $color-label-gray;
    color: $color-text-and-object-medium-emphasis;
  }

  &--premium {
    background-color: $color-label-yellow;
    color: $color-text-and-object-high-emphasis-inverse;
  }

  &--enterprise {
    background-color: $color-label-blue;
    color: $color-text-and-object-high-emphasis-inverse;
  }
}

.v20222chip {
  background-color: $color-surface-tertiary;
  color: $color-text-and-object-high-emphasis;
  display: inline-block;
  padding: 0.5em 1em;
  border-radius: $border-radius-rounder;
  font-size: 1em;
  font-weight: bold;
  line-height: 1;
  white-space: nowrap;
}

.v20221chip {
  display: inline-flex;
  align-items: center;

  &__icon {
    flex: 1 0 auto;
    font-size: 2em;
  }

  &__text {
    font-size: 1em;
    font-weight: bold;

    &:nth-child(n + 2) {
      margin-left: 0.2em;
    }
  }
}

.v20231chip {
  display: inline-flex;
  align-items: center;

  &__icon {
    flex: 1 0 auto;
    font-size: 2em;
  }

  &__text {
    font-size: 1em;
    font-weight: bold;

    &:nth-child(n + 2) {
      margin-left: 0.2em;
    }
  }
}

.v20231ManageOnlyFreeChip {
  display: inline-flex;
  align-items: center;

  &__icon {
    flex: 1 0 auto;
    font-size: 2em;
  }

  &__text {
    font-size: 1em;
    font-weight: bold;

    &:nth-child(n + 2) {
      margin-left: 0.2em;
    }
  }
}
</style>
